import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import akshayPoster from '../../assets/testimonials/akshay.jpg';
import rohanPoster from '../../assets/testimonials/rohan.jpg';
import kunalPoster from '../../assets/testimonials/kunal.jpg';
import adityaPoster from '../../assets/testimonials/aditya.jpg';
import harshPoster from '../../assets/testimonials/harsh.jpg';
import arjunPoster from '../../assets/testimonials/arjun.jpg';
import mohitPoster from '../../assets/testimonials/mohit.jpg';
import vivekPoster from '../../assets/testimonials/vivek.jpg';
import nikhilPoster from '../../assets/testimonials/nikhil.jpg';
import varunPoster from '../../assets/testimonials/varun.jpg';
import prajwalPoster from '../../assets/testimonials/prajwal.jpg';
import rajPoster from '../../assets/testimonials/raj.jpg';
import hetviPoster from '../../assets/testimonials/hetvi.jpg';

// Serve through CloudFront rather than hitting the S3 bucket directly.
const ASSETS_URL = (process.env.REACT_APP_ASSETS_URL || 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com')
  .replace(/\/$/, '');

// The originals are 4K/8K HEVC at ~60Mbps (up to 230MB each). Flip this to true
// once scripts/optimize-testimonial-videos.sh has published the re-encoded set
// to video/optimized/, which is ~60x smaller and H.264 rather than HEVC.
const USE_OPTIMIZED = false;

const videoUrl = (t) =>
  USE_OPTIMIZED
    ? `${ASSETS_URL}/video/optimized/${t.id}.mp4`
    : `${ASSETS_URL}/video/${t.file}`;

const useStyles = makeStyles((theme) => ({
  testimonialsContainer: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    // First light section under the hero, so it carries the rounded cap that
    // closes off the hero's dark gradient (CaseClientsSection used to).
    borderRadius: '24px 24px 0 0',
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(15),
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
      paddingTop: theme.spacing(12),
      paddingBottom: theme.spacing(12),
    },
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(10),
      paddingBottom: theme.spacing(10),
    },
  },
  heading: {
    ...theme.typography['h3-bold'],
    color: '#2E3558',
    textAlign: 'center',
    marginBottom: theme.spacing(10),
    padding: theme.spacing(0, 5),
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h4-bold'],
      marginBottom: theme.spacing(8),
    },
  },
  cardsWrapper: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
  },
  '@keyframes scrollLeft': {
    '0%': { transform: 'translateX(0)' },
    '100%': { transform: 'translateX(-50%)' },
  },
  '@keyframes scrollRight': {
    '0%': { transform: 'translateX(-50%)' },
    '100%': { transform: 'translateX(0)' },
  },
  scrollTrack: {
    display: 'flex',
    gap: theme.spacing(4),
    width: 'max-content',
    animation: '$scrollLeft 35s linear infinite',
    '&:hover': {
      animationPlayState: 'paused',
    },
  },
  scrollTrackReverse: {
    display: 'flex',
    gap: theme.spacing(4),
    width: 'max-content',
    marginTop: theme.spacing(4),
    animation: '$scrollRight 40s linear infinite',
    '&:hover': {
      animationPlayState: 'paused',
    },
  },
  // Applied while the section is scrolled out of the viewport. Nobody can see the
  // marquee then, so there is no reason to keep the compositor animating it.
  trackPaused: {
    animationPlayState: 'paused !important',
  },
  testimonialCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E4E4E7',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    padding: theme.spacing(3),
    width: '260px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  userName: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '14px',
    fontWeight: 700,
    color: '#3D50C8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userHandle: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    color: '#71717A',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  videoWrapper: {
    width: '100%',
    height: '300px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
    // Shows through until the poster has decoded.
    background: 'linear-gradient(160deg, #2E3558 0%, #1A1A2E 100%)',
  },
  poster: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  videoPlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  playButton: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
      transform: 'scale(1.08)',
    },
  },
  playIcon: {
    color: theme.palette.shades['clr-white-900'],
    fontSize: '28px !important',
  },
  video: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  // Text-testimonial body — same footprint as videoWrapper (300px tall) so the
  // text cards line up exactly with the video cards.
  textWrapper: {
    width: '100%',
    height: '300px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    padding: theme.spacing(3),
    background: 'linear-gradient(160deg, #EEF2FF 0%, #F8F9FF 100%)',
    border: '1px solid #E7EAF5',
    display: 'flex',
    alignItems: 'flex-start',
  },
  // 12px keeps even the longest testimonial (~335 chars) inside the card.
  testimonialText: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.55,
    color: '#3F3F46',
  },
}));

const testimonials = [
  { id: 'akshay', name: 'Akshay', handle: '@Ashoka University', file: '11.+Akshay+%E2%80%93+Ashoka.mp4', poster: akshayPoster },
  { id: 'rohan', name: 'Rohan', handle: '@BITS Pilani', file: '2.+Rohan+%E2%80%93+BITS+Pilani.mp4', poster: rohanPoster },
  { id: 'kunal', name: 'Kunal', handle: '@NIT Trichy', file: '3.+Kunal+%E2%80%93+NIT+Trichy.mp4', poster: kunalPoster },
  { id: 'aditya', name: 'Aditya', handle: '@SRM', file: '4.+Aditya+%E2%80%93+SRM.mp4', poster: adityaPoster },
  { id: 'harsh', name: 'Harsh', handle: '@VNIT Nagpur', file: '5.+Harsh+%E2%80%93+VNIT+Nagpur.mp4', poster: harshPoster },
  { id: 'arjun', name: 'Arjun', handle: '@IIT Bombay', file: '6.+Arjun+%E2%80%93+IIT+Bombay.mp4', poster: arjunPoster },
  { id: 'mohit', name: 'Mohit', handle: '@Manipal', file: '8.+Mohit+%E2%80%93+Manipal.mp4', poster: mohitPoster },
  { id: 'vivek', name: 'Vivek', handle: '@Christ University', file: '9.+Vivek+%E2%80%93+Christ+University.mp4', poster: vivekPoster },
  { id: 'nikhil', name: 'Nikhil', handle: '@IIT Kharagpur', file: '10.+Nikhil+%E2%80%93+IIT+Kharagpur.mp4', poster: nikhilPoster },
  { id: 'varun', name: 'Varun', handle: '@varun', file: 'varun.mp4', poster: varunPoster },
  { id: 'prajwal', name: 'Prajwal Sharma', handle: '@Team Alpha', file: 'Prajwal+Sharma_Team+Alpha.mp4', poster: prajwalPoster },
  { id: 'raj', name: 'Raj Nigam', handle: '@Raj Nigam', file: 'Raj_Nigam.mp4', poster: rajPoster },
  { id: 'hetvi', name: 'Hetvi', handle: '@Hetvi', file: 'Hetvi.mp4', poster: hetviPoster },
];

const textTestimonials = [
  {
    name: 'Aarav',
    handle: '@IIT Delhi',
    text: "Hi, I'm Aarav currently pursuing BBA from Indian Institute of Technology Delhi preparing for product roles. Earlier, I struggled to find structured PM prep resources and often felt lost during case practice. CaseMasterAI gave me practical frameworks and realistic case questions that made my preparation much more focused and effective.",
  },
  {
    name: 'Rohan',
    handle: '@BITS Pilani',
    text: "Hey, I'm Rohan currently pursuing MBA from Birla Institute of Technology and Science, Pilani. I used to struggle with structuring my thoughts during consulting case interviews. After using CaseMaster, I learned practical frameworks that helped me approach cases much more confidently.",
  },
  {
    name: 'Kunal',
    handle: '@NIT Trichy',
    text: "Hi, I'm Kunal currently pursuing BBA from National Institute of Technology Tiruchirappalli. My biggest issue was not knowing how to practice product and strategy cases effectively. CaseMaster gave me a structured way to prepare and improved my problem-solving significantly.",
  },
  {
    name: 'Aditya',
    handle: '@SRM',
    text: "Hi, I'm Aditya currently pursuing MBA from SRM Institute of Science and Technology. I was confused about how to even begin product management prep. The CaseMaster provided a clear roadmap and practical frameworks that made the learning process much easier.",
  },
  {
    name: 'Harsh',
    handle: '@VNIT Nagpur',
    text: "Hey, I'm Harsh currently pursuing BBA 2nd year from Visvesvaraya National Institute of Technology Nagpur. Earlier, my case answers lacked structure and clarity. Using Case Master helped me organize my thinking better and approach interviews more strategically.",
  },
  {
    name: 'Arjun',
    handle: '@IIT Bombay',
    text: "Hi, I'm Arjun currently pursuing MBA from Indian Institute of Technology Bombay. I struggled to get exposure to real strategy and founder's office type business problems. CaseMaster gave me practical cases that helped build my business thinking.",
  },
  {
    name: 'Rohit',
    handle: '@LPU',
    text: "Hi, I'm Rahul currently pursuing BCA from LPU. Before using this platform, consulting and product case prep felt overwhelming because I didn't know where to start or how to structure my answers. The platform broke everything down into simple, practical frameworks that made prep far more manageable.",
  },
  {
    name: 'Mohit',
    handle: '@Manipal',
    text: "Hi, I'm Mohit currently pursuing MBA from Manipal Academy of Higher Education. My preparation used to be scattered and unorganized — I was consuming random resources without much progress. CaseMasterAI gave me a structured approach and helped me focus only on what actually matters for interviews.",
  },
  {
    name: 'Vivek',
    handle: '@Christ University',
    text: "Hey, I'm Vivek currently pursuing MBA from Christ University. Earlier, frameworks always felt too theoretical and difficult to apply in interviews. CaseMasterAI showed me how to use them in actual business and product cases, which made my prep much more practical.",
  },
  {
    name: 'Nikhil',
    handle: '@IIT Kharagpur',
    text: "Hi, I'm Nikhil currently pursuing MBA from Indian Institute of Technology Kharagpur. I struggled with both speed and structure while solving product cases under time pressure. Practicing on CaseMaster AI helped me improve both and made me feel much more interview-ready.",
  },
  {
    name: 'Akshay',
    handle: '@Ashoka University',
    text: "Hi, I'm Akshay currently pursuing 2nd year BBA from Ashoka University. My biggest challenge was developing structured analytical thinking for strategy and business roles. The curated cases here helped me build that mindset and improve how I break down complex problems.",
  },
];

// The <video> that is currently playing, so starting one can stop the previous.
let activeVideo = null;

/**
 * Renders a poster image and only creates the <video> element once the user
 * clicks play. The marquee sweeps every card through the viewport, so mounting
 * a <video> per card meant 26 media players all range-requesting their source.
 */
const TestimonialCard = ({ t, classes }) => {
  const [activated, setActivated] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);
  const videoRef = React.useRef(null);

  React.useEffect(() => () => {
    if (activeVideo === videoRef.current) activeVideo = null;
  }, []);

  const handleActivate = () => {
    if (activated) videoRef.current?.play();
    else setActivated(true);
  };

  const handlePlay = () => {
    if (activeVideo && activeVideo !== videoRef.current) activeVideo.pause();
    activeVideo = videoRef.current;
    setPlaying(true);
  };

  return (
    <Box className={classes.testimonialCard}>
      <Box className={classes.cardHeader}>
        <Typography className={classes.userName}>{t.name}</Typography>
        <Typography className={classes.userHandle}>{t.handle}</Typography>
      </Box>
      <Box className={classes.videoWrapper}>
        <img
          src={t.poster}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className={classes.poster}
        />
        {!playing && (
          <Box className={classes.videoPlaceholder} onClick={handleActivate}>
            <Box className={classes.playButton}>
              <PlayArrowIcon className={classes.playIcon} />
            </Box>
          </Box>
        )}
        {activated && (
          <video
            ref={videoRef}
            src={videoUrl(t)}
            preload="auto"
            autoPlay
            controls
            className={classes.video}
            onPlay={handlePlay}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
          />
        )}
      </Box>
    </Box>
  );
};

const TextTestimonialCard = ({ t, classes }) => (
  <Box className={classes.testimonialCard}>
    <Box className={classes.cardHeader}>
      <Typography className={classes.userName}>{t.name}</Typography>
      <Typography className={classes.userHandle}>{t.handle}</Typography>
    </Box>
    <Box className={classes.textWrapper}>
      <Typography className={classes.testimonialText}>{t.text}</Typography>
    </Box>
  </Box>
);

const CaseTestimonials = () => {
  const classes = useStyles();
  const wrapperRef = React.useRef(null);
  const [inView, setInView] = React.useState(true);

  const doubled = React.useMemo(() => [...testimonials, ...testimonials], []);
  const doubledText = React.useMemo(() => [...textTestimonials, ...textTestimonials], []);

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pausedClass = inView ? '' : classes.trackPaused;

  return (
    <Box className={classes.testimonialsContainer}>
      <Typography className={classes.heading}>
        Trusted by 6000+ candidates
      </Typography>

      <Box className={classes.cardsWrapper} ref={wrapperRef}>
        <Box className={`${classes.scrollTrack} ${pausedClass}`}>
          {doubled.map((t, index) => (
            <TestimonialCard key={`${t.id}-${index}`} t={t} classes={classes} />
          ))}
        </Box>

        <Box className={`${classes.scrollTrackReverse} ${pausedClass}`}>
          {doubledText.map((t, index) => (
            <TextTestimonialCard key={`${t.handle}-${index}`} t={t} classes={classes} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CaseTestimonials;
